import { EventBus } from '../../core/events/EventBus';
import { DuelService } from '../../services/DuelService';
import { AuthStore } from '../../core/store/authStore';
import { ApiClient } from '../../services/ApiClient';
import { ToastManager } from './Toast';

export class DuelResultModal {
  private static overlayEl: HTMLElement | null = null;

  static show(myScore: number) {
    if (this.overlayEl) return;

    const overlay = document.createElement('div');
    overlay.className = 'ad-interstitial-overlay';
    overlay.style.zIndex = '1000000';
    overlay.style.backdropFilter = 'blur(12px)';

    this.overlayEl = overlay;
    document.body.appendChild(overlay);

    this.renderContent(myScore);

    // Listen to opponent finish if they haven't yet
    EventBus.on('duel:finished_by_opponent', () => {
      this.renderContent(myScore);
    });
  }

  private static async renderContent(myScore: number) {
    if (!this.overlayEl || !DuelService.activeDuel) return;
    
    const user = AuthStore.getUser();
    const oppFinished = DuelService.opponentFinished;
    const oppScore = DuelService.opponentScore;

    let resultText = 'Bekleniyor...';
    let resultColor = '#fbbf24';
    
    if (oppFinished) {
      if (myScore > oppScore) {
        resultText = '🏆 Kazandın!';
        resultColor = '#22c55e';
      } else if (myScore < oppScore) {
        resultText = '💀 Kaybettin!';
        resultColor = '#ef4444';
      } else {
        resultText = '🤝 Berabere!';
        resultColor = '#38bdf8';
      }
    }

    this.overlayEl.innerHTML = `
      <div class="glass-panel" style="width:90%;max-width:440px;padding:36px;border-radius:24px;background:#0f172a;border:2px solid ${resultColor};display:flex;flex-direction:column;gap:20px;position:relative;align-items:center;text-align:center;box-shadow: 0 0 50px ${resultColor}66;">
        <h2 style="font-size:2rem;font-weight:900;color:${resultColor};text-transform:uppercase;letter-spacing:2px;margin:0;">
          ${resultText}
        </h2>
        
        <div style="display:flex;justify-content:space-between;width:100%;background:#1e293b;border-radius:16px;padding:20px;margin-top:10px;">
          <div style="display:flex;flex-direction:column;align-items:center;flex:1;">
            <div style="font-size:0.85rem;color:#94a3b8;font-weight:700;margin-bottom:8px;">${user?.username}</div>
            <div style="font-size:2rem;font-weight:900;color:#38bdf8;">${myScore}</div>
          </div>
          
          <div style="display:flex;flex-direction:column;justify-content:center;font-size:1.5rem;">⚔️</div>
          
          <div style="display:flex;flex-direction:column;align-items:center;flex:1;">
            <div style="font-size:0.85rem;color:#94a3b8;font-weight:700;margin-bottom:8px;">Rakip</div>
            <div style="font-size:2rem;font-weight:900;color:${oppFinished ? '#f43f5e' : '#64748b'};">
              ${oppFinished ? oppScore : '...'}
            </div>
          </div>
        </div>

        ${!oppFinished ? `
          <div style="color:#fbbf24;font-size:0.9rem;display:flex;align-items:center;gap:8px;">
            <span class="spinner" style="width:16px;height:16px;border:2px solid #fbbf24;border-top-color:transparent;border-radius:50%;animation:spin 1s linear infinite;display:inline-block;"></span>
            Rakibin oyunu bitirmesi bekleniyor...
          </div>
        ` : `
          <div style="display:flex;gap:12px;width:100%;margin-top:10px;">
            <button id="close-duel-btn" style="flex:1;padding:12px;background:#334155;color:#f8fafc;border:none;border-radius:12px;font-weight:700;cursor:pointer;transition:background 0.2s;">Kapat</button>
            <button id="congrats-duel-btn" style="flex:1;padding:12px;background:linear-gradient(135deg,#eab308,#ca8a04);color:#fff;border:none;border-radius:12px;font-weight:800;cursor:pointer;box-shadow:0 4px 15px rgba(234,179,8,0.4);">👏 Tebrik Et</button>
            <button id="rematch-duel-btn" style="flex:1;padding:12px;background:linear-gradient(135deg,#f43f5e,#e11d48);color:#fff;border:none;border-radius:12px;font-weight:800;cursor:pointer;box-shadow:0 4px 15px rgba(225,29,72,0.4);">Rövanş İste</button>
          </div>
        `}
        <style>@keyframes spin { to { transform: rotate(360deg); } }</style>
      </div>
    `;

    // Bind buttons
    if (oppFinished) {
      if (myScore > oppScore && user && DuelService.activeDuel) {
        const loserId = DuelService.activeDuel.challenger_id === user.id ? DuelService.activeDuel.opponent_id : DuelService.activeDuel.challenger_id;
        // Only the winner submits the result to avoid race conditions or duplicate submissions
        ApiClient.submitDuelResult(DuelService.activeDuel.id, user.id, loserId);

        // Update local user state for immediate feedback
        const updatedUser = { ...user };
        updatedUser.duels_won = (updatedUser.duels_won || 0) + 1;
        updatedUser.win_streak = (updatedUser.win_streak || 0) + 1;
        AuthStore.setUser(updatedUser);

        // Check Achievements
        import('../../services/AchievementService').then(({ AchievementService }) => {
          AchievementService.checkDuelAchievements(updatedUser);
        });
      } else if (myScore < oppScore && user && DuelService.activeDuel) {
        // Loser local reset
        const updatedUser = { ...user };
        updatedUser.win_streak = 0;
        AuthStore.setUser(updatedUser);
      }

      this.overlayEl.querySelector('#close-duel-btn')?.addEventListener('click', () => {
        DuelService.activeDuel = null;
        this.close();
      });

      this.overlayEl.querySelector('#congrats-duel-btn')?.addEventListener('click', async (e) => {
        const btn = e.currentTarget as HTMLButtonElement;
        btn.disabled = true;
        btn.innerText = 'Gönderildi!';
        btn.style.opacity = '0.7';

        if (DuelService.activeDuel) {
          const oppId = DuelService.activeDuel.challenger_id === user?.id 
            ? DuelService.activeDuel.opponent_id 
            : DuelService.activeDuel.challenger_id;
          
          import('../../services/DuelService').then(async ({ DuelService }) => {
            await DuelService.sendCongrats(oppId);
          });
        }
      });

      this.overlayEl.querySelector('#rematch-duel-btn')?.addEventListener('click', async () => {
        if (!DuelService.activeDuel) return;
        const oppId = DuelService.activeDuel.challenger_id === user?.id 
          ? DuelService.activeDuel.opponent_id 
          : DuelService.activeDuel.challenger_id;
        const gameId = DuelService.activeDuel.game_id;
        
        DuelService.activeDuel = null; // Clear old duel
        this.close();
        
        ToastManager.show('Rövanş isteği gönderildi!', 'info');
        await DuelService.sendInvite(oppId, gameId);
      });
    }
  }

  static close() {
    if (this.overlayEl) {
      this.overlayEl.remove();
      this.overlayEl = null;
    }
  }
}
